import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonAsyncio = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Asyncio: High-Scale Concurrency</h1>
                <p className="subtitle">Mastering the Event Loop. How to handle 10,000 connections with a single thread.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        How can one person play 50 games of chess at once? A <strong>Threads</strong> approach would be hiring 50 players.
                        <strong>Asyncio</strong> is like a Grandmaster:
                    </p>
                    <ul>
                        <li><strong>The Move (`await`):</strong> The Grandmaster walks to Table 1, makes a move, and walks away immediately while the opponent thinks.</li>
                        <li><strong>The Loop:</strong> They circle the room. If Table 2 is ready, they move. If Table 3 is still thinking, they skip it.</li>
                        <li><strong>The Efficiency:</strong> One person (Single Thread) keeps 50 games moving because the "waiting" is done by the opponents (I/O), not the Grandmaster.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The Syntax: `async` and `await`</h2>
                <p>
                    Asyncio introduces a new syntax. A function defined with `async def` is a <strong>Coroutine</strong>. It doesn't run when you call it; it returns a coroutine object that must be scheduled.
                </p>
                <CodeBlock language="python" code={`import asyncio

# 1. Define a Coroutine
async def say_hello():
    print("Hello...")
    # 2. Pause execution here for 1 second (Yield control)
    await asyncio.sleep(1) 
    print("...World!")

# 3. Running it (The classic entry point)
# asyncio.run() starts the Event Loop
asyncio.run(say_hello())`} />
            </div>

            <div className="content-section">
                <h2>3. Running Concurrently: `asyncio.gather`</h2>
                <p>
                    The power comes when you run multiple things at once.
                    `asyncio.gather` schedules multiple coroutines and waits for ALL of them to finish.
                </p>
                <CodeBlock language="python" code={`import asyncio
import time
import { ProTip } from '../../components/LessonUI';

async def brew_coffee():
    print(" Brewing coffee...")
    await asyncio.sleep(2) # Simulates I/O
    return "Coffee Ready"

async def toast_bread():
    print(" Toasting bread...")
    await asyncio.sleep(2)
    return "Toast Ready"

async def main():
    start = time.time()
    
    # Run both AT THE SAME TIME
    results = await asyncio.gather(brew_coffee(), toast_bread())
    
    duration = time.time() - start
    print(f"Breakfast: {results}")
    print(f"Total time: {duration:.2f}s") 
    # Output: 2.01s (Not 4s!)

asyncio.run(main())`} />
            </div>

            <div className="content-section">
                <h2>4. Deep Dive: The Event Loop & Blocking</h2>
                <p>
                    The <strong>Event Loop</strong> is a `while True` loop that runs tasks.
                    <strong>CRITICAL RULE:</strong> Never do heavy math or `time.sleep()` inside an async function. It pauses the ENTIRE loop.
                </p>
                <CodeBlock language="python" code={`#  BAD
async def bad_task():
    time.sleep(10) # FREEZES the whole program for 10s! 
    # No other request can be handled.

# GOOD
async def good_task():
    await asyncio.sleep(10) # Yields control. 
    # Other tasks run for 10s.`} />
            </div>

            <div className="content-section">
                <h2>5. Task Scheduling: `create_task`</h2>
                <p>
                    Sometimes you want "Fire and Forget" mechanics. `create_task` submits a coroutine to the loop immediately, allowing the code to proceed without waiting for it.
                </p>
                <CodeBlock language="python" code={`async def background_logger():
    # Runs in the background
    await asyncio.sleep(1)
    print("Log saved!")

async def main():
    task = asyncio.create_task(background_logger()) # Starts NOW
    
    print("Doing work...")
    await asyncio.sleep(0.5)
    
    await task # Wait for it eventually`} />
            </div>

            <div className="content-section">
                <ProTip><strong>Modern Python:</strong> Libraries like <strong>FastAPI</strong> and <strong>Discord.py</strong> are built entirely on `asyncio`. It is the future of high-performance Python web services.</ProTip>
            </div>

        </div>
    );
};

export default PythonAsyncio;

